import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref
} from "vue";
import { useRoute, useRouter } from "vue-router";
import Service, { IJitsiConfigOverride } from "@/service";
import { Infrastructure } from "@/infrastructure";
import { redirect } from "@/middlewares/auth-middleware";
import Popup from "@/components/popup/popup.vue";
import { useResizeAdjuster } from "@/composables/resize-adjuster";
import { jwtDecode } from "jwt-decode";
import RatingStars from "@/components/rating-stars/rating-stars.vue";
import TNTextarea from "@/components/textarea/textarea.vue";
import { SmartCaptcha } from "vue3-smart-captcha";
import { IReadyToCloseEvent, LinkOpeningSetting } from "@/views/meet/i-meet";
import Checkbox from "@/components/checkbox/checkbox.vue";

const desktopUserAgentRegex =
  /(Mac OS|MacPPC|MacIntel|Mac_PowerPC|Macintosh|Mac OS X|Windows)/i;
const mobileUserAgentRegex = /(Android|iPhone|iPad|iPod)/i;
const platformTemplates = [
  {
    regex: /(Windows)/i,
    link: window.VUE_APP_WINDOWS_LINK
  },
  {
    regex: /(Mac OS|MacPPC|MacIntel|Mac_PowerPC|Macintosh|Mac OS X)/i,
    link: window.VUE_APP_MACOS_LINK
  }
];

export default defineComponent({
  name: "MeetPage",
  components: {
    Checkbox,
    TNTextarea,
    Popup,
    RatingStars,
    SmartCaptcha
  },
  setup: () => {
    const route = useRoute();
    const router = useRouter();
    useResizeAdjuster();
    const service = new Service();
    const Infra = new Infrastructure();

    const isWaitingForEnter = ref<boolean>(true);
    const isConnecting = ref<boolean>(true);
    const meetContainer = ref<HTMLElement | null>(null);
    const errorMessage = ref<string>("");
    const isShowSelectPlatform = ref<boolean>(false);
    const isReviewPageVisible = ref<boolean>(false);
    const review = ref<{ stars: number; comment: string; sent: boolean }>({
      stars: 0,
      comment: "",
      sent: false
    });
    const commentErrorMessage = ref<string>("");
    const captchaToken = ref<string>("");
    const captchaSiteKey = ref<string>(window.VUE_APP_YANDEX_CAPTCHA_SITE_KEY);
    const isConfirmingCaptcha = ref<boolean>(false);
    const isShowAuthOptions = ref<boolean>(false);
    const isRedirecting = ref<boolean>(false);
    const isReviewSending = ref<boolean>(false);
    const isShowCheckbox = ref<boolean>(false);
    const isSaveChoice = ref<boolean>(false);
    const isOpeningWeb = ref<boolean>(false);
    const isOpeningApp = ref<boolean>(false);
    const isNotFound = ref<boolean>(false);

    const roomId: string = route.path.split("/").pop() || "";
    const noAuth: boolean = route.query.auth === "no-auth";
    const main = document.querySelector("main") as HTMLElement;
    const webLink = window.VUE_APP_WEB_APP_URL;
    const isMobile = mobileUserAgentRegex.test(navigator.userAgent);
    const detectedPlatform = platformTemplates.find(
      sc => !isMobile && sc.regex.test(navigator.userAgent) && sc.link
    );
    let showReviewPage: boolean = false;
    let meetUrl: string = "";
    let user: string | undefined;

    onMounted(() => {
      void initPage();
      window.addEventListener("message", readyToCloseEventWatcher);
    });
    onBeforeUnmount(() => {
      main.style.height = "";
      window.removeEventListener("message", readyToCloseEventWatcher);
    });

    const loadingTitle = computed<string>(() =>
      isConnecting.value || isRedirecting.value
        ? "Инициализация звонка"
        : "Пожалуйста, подождите"
    );
    const loadingSubtitle = computed<string>(() =>
      isConnecting.value || isRedirecting.value
        ? "Идёт получение данных встречи"
        : "Организатор скоро пустит вас в собрание"
    );
    const reviewTitle = computed<string>(() =>
      review.value.sent ? "Спасибо за вашу оценку!" : "Вы покинули встречу"
    );
    const reviewSubtitle = computed<string>(() =>
      review.value.sent
        ? "Ваша обратная связь помогает делать сервис лучше!"
        : "Оцените работу сервиса"
    );
    const reviewButtonDisabled = computed<boolean>(
      () =>
        !review.value.stars ||
        !!commentErrorMessage.value ||
        isReviewSending.value
    );
    const landingHeaderText = computed<string>(() => {
      if (isOpeningWeb.value) return "Открываем в браузере…";
      if (isOpeningApp.value) return "Открываем в приложении…";

      return "Присоединиться ко встрече TN Life";
    });
    const errorPopupTitle = computed<string>(() =>
      isNotFound.value ? "Встреча не найдена" : "Ошибка"
    );

    const initPage = async () => {
      const token = localStorage.getItem("tn-meet__access-token");

      if (token) {
        const jwtData = jwtDecode<{
          portal_code?: string;
          partner_fio?: string;
          sub?: string;
        }>(token);
        if (jwtData.portal_code) {
          isShowSelectPlatform.value = true;
        }
        if (jwtData.sub) {
          const domain = location.hostname
            .split(".")
            .reverse()
            .filter((_, i) => i <= 1)
            .reverse()
            .join(".");
          const expires = new Date();

          expires.setMonth(expires.getMonth() + 12);

          document.cookie = `user_meet=${jwtData.sub}; Expires=${expires.toUTCString()}; Domain=${domain}; Path=/`;
        }
        if (await getAndApplyUserSettings()) return;
      } else if (!noAuth) {
        void redirect(roomId, false, true);

        return;
      }
      void getReviewConfig();
      await nextTick();
      void enableMeet();
    };
    const enableMeet = async (
      skipNoAuth: boolean = false,
      waiting: boolean = false
    ) => {
      if (!waiting) {
        isConnecting.value = true;
      }
      if (meetContainer.value) {
        await service.pause(1000);
        try {
          let config: IJitsiConfigOverride | undefined;
          if (route.hash) {
            config = service.parseMeetUrl(location.href)?.options
              .configOverwrite;
          }
          if (!meetUrl) {
            meetUrl = await Infra.getMeetUrl(roomId);
          }
          isConnecting.value = false;

          loadPlugin(meetUrl);
          const token = localStorage.getItem("tn-meet__access-token");

          if (!skipNoAuth && !token) {
            isShowAuthOptions.value = true;
            return;
          }

          const meet = service.parseMeetUrl(meetUrl, config);

          if (meet) {
            meet.options.parentNode = meetContainer.value;
            localStorage.setItem("jitsiLocalStorage", "{}");
            const jitsi = new JitsiMeetExternalAPI(meet.domain, meet.options);
            jitsi.addListener("readyToClose", () => {
              endMeetHandler();
            });
            isWaitingForEnter.value = false;
          } else {
            errorMessage.value = "При инициализации встречи произошла ошибка";
          }
        } catch (error: any) {
          isConnecting.value = false;
          if (error?.status === 449) {
            await service.pause(1000);
            void enableMeet(false, true);
          } else if (
            error &&
            ["Invalid token", "Token expired"].includes(error.message)
          ) {
            try {
              await Infra.refresh();
              void enableMeet();
            } catch {
              isRedirecting.value = true;
              void redirect(roomId);
            }
          } else if (error?.status === 400) {
            void router.push({
              name: "index",
              query: {
                errorMessage: "Неверный код встречи"
              }
            });
          } else if (error?.status === 403) {
            isRedirecting.value = true;
            void redirect(roomId);
          } else if (error?.status === 404) {
            isNotFound.value = true;
            errorMessage.value =
              "Проверьте правильность ввода кода встречи и попробуйте заново";
            isWaitingForEnter.value = false;
          } else if (String(error.message).includes("JitsiMeetExternalAPI")) {
            setTimeout(() => {
              enableMeet();
            }, 100);

            return;
          } else {
            isWaitingForEnter.value = false;
            errorMessage.value =
              "При инициализации встречи произошла ошибка: " + error?.message ||
              "";
          }
        }
      }
    };
    const errorButtonClickHandler = () => {
      errorMessage.value = "";
      isWaitingForEnter.value = true;
      isConnecting.value = true;
      isNotFound.value = false;
      clearContainer();
      void enableMeet();
    };
    const clearContainer = () => {
      if (meetContainer.value) {
        meetContainer.value
          .querySelectorAll("iframe")
          .forEach(iframe => iframe.remove());
      }
    };
    const goToHomepage = () => {
      void router.push({
        name: "index",
        query: {
          meet: roomId
        }
      });
    };
    const openWebMeeting = async () => {
      if (webLink) {
        if (isSaveChoice.value) {
          try {
            await Infra.updateOpeningSetting(
              LinkOpeningSetting.ALWAYS_IN_BROWSER
            );
          } catch (e: any) {
            if (e.status === 401) {
              try {
                await Infra.refresh();
                void openWebMeeting();
              } catch {
                void redirect(roomId);
              }
              return;
            }
          }
        }

        openWeb();
      }
    };
    const openAppMeeting = async () => {
      if (isSaveChoice.value) {
        try {
          await Infra.updateOpeningSetting(LinkOpeningSetting.ALWAYS_IN_APP);
        } catch (e: any) {
          if (e.status === 401) {
            try {
              await Infra.refresh();
              void openAppMeeting();
            } catch {
              void redirect(roomId);
            }
            return;
          }
        }
      }
      openApp();
    };
    const downloadAppMeeting = () => {
      if (detectedPlatform) window.open(detectedPlatform.link, "_blank");
    };
    const confirmCaptcha = () => {
      if (captchaSiteKey.value) {
        isConfirmingCaptcha.value = true;
      }
    };
    const sendCallQualityReview = async () => {
      isReviewSending.value = true;
      try {
        await Infra.callQualityReview({
          roomID: roomId,
          quality: review.value.stars,
          comment: review.value.comment.trim(),
          captcha_token: captchaToken.value
        });
      } catch (e: any) {
        if (e.status === 401) {
          try {
            await Infra.refresh();
            void sendCallQualityReview();

            return;
          } catch {}
        }
      }
      review.value.sent = true;
      isReviewSending.value = false;
    };
    const reenterMeeting = () => {
      isReviewPageVisible.value = false;
      isConfirmingCaptcha.value = false;
      review.value = {
        stars: 5,
        comment: "",
        sent: false
      };
      void nextTick(() => {
        void enableMeet();
      });
    };
    const getReviewConfig = async () => {
      try {
        showReviewPage = (await Infra.getConfig()).showCallQualityReviewPage;
      } catch {}
    };
    const updateCommentHandler = (value: string) => {
      if (value.trim().length > 1000) {
        commentErrorMessage.value = "Максимальное количество знаков: 1000";
      } else {
        commentErrorMessage.value = "";
      }
    };
    const captchaCallback = (token: string) => {
      captchaToken.value = token;
      void sendCallQualityReview();
    };
    const enterWithoutAuth = async () => {
      isShowAuthOptions.value = false;
      await nextTick();
      void enableMeet(true);
    };
    const enterWithAuth = () => {
      void redirect(roomId);
    };
    const loadPlugin = (link: string) => {
      const linkData = new URL(link);
      if (
        document.querySelector(
          `script[src="${linkData.origin}/external_api.js"]`
        )
      )
        return;
      const plugin = document.createElement("script");

      plugin.setAttribute("src", linkData.origin + "/external_api.js");
      document.head.appendChild(plugin);
    };
    const endMeetHandler = () => {
      if (showReviewPage) {
        isReviewPageVisible.value = true;
      } else {
        goToHomepage();
      }
    };
    const readyToCloseEventWatcher = (event: MessageEvent) => {
      let messageData: IReadyToCloseEvent = {};

      if (typeof event.data !== "string") {
        messageData = event.data;
      } else {
        try {
          messageData = JSON.parse(event.data);
        } catch {}
      }

      if (
        messageData.method === "message" &&
        messageData.params?.data?.name === "video-ready-to-close"
      ) {
        endMeetHandler();
      }
    };
    const getAndApplyUserSettings = async (): Promise<boolean> => {
      user = document.cookie
        .match(/user_meet=[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}/i)?.[0]
        ?.replace("user_meet=", "");

      if (!user) {
        user = document.cookie
          .match(/user_life=[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}/i)?.[0]
          ?.replace("user_life=", "");
      }

      isShowCheckbox.value = !!user;

      if (user) {
        try {
          const userSetting = await Infra.getOpeningSetting(user);

          if (userSetting) {
            if (
              userSetting === LinkOpeningSetting.ALWAYS_IN_APP &&
              service.validateRoomId(roomId) &&
              desktopUserAgentRegex.test(navigator.userAgent) &&
              !mobileUserAgentRegex.test(navigator.userAgent)
            ) {
              openApp(true);

              return true;
            } else if (userSetting === LinkOpeningSetting.ALWAYS_IN_BROWSER) {
              openWeb();

              return true;
            }
          }
        } catch {}
      }
      return false;
    };
    const openWeb = () => {
      isOpeningWeb.value = true;
      location.assign(webLink.replace(/\/?$/, "/meet/") + roomId);
    };
    const openApp = (auto: boolean = false) => {
      isOpeningApp.value = true;
      setTimeout(
        () => {
          location.assign("tnlife://meet/" + roomId);
        },
        auto ? 400 : 0
      );
    };

    return {
      isWaitingForEnter,
      meetContainer,
      errorMessage,
      isConnecting,
      isShowSelectPlatform,
      webLink,
      detectedPlatform,
      loadingTitle,
      loadingSubtitle,
      isReviewPageVisible,
      review,
      reviewTitle,
      reviewSubtitle,
      commentErrorMessage,
      captchaSiteKey,
      captchaToken,
      isConfirmingCaptcha,
      isShowAuthOptions,
      reviewButtonDisabled,
      isShowCheckbox,
      isSaveChoice,
      landingHeaderText,
      errorPopupTitle,
      endMeetHandler,
      enterWithAuth,
      enterWithoutAuth,
      confirmCaptcha,
      captchaCallback,
      updateCommentHandler,
      reenterMeeting,
      downloadAppMeeting,
      openAppMeeting,
      openWebMeeting,
      goToHomepage,
      errorButtonClickHandler
    };
  }
});
